import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/a11y'

const ResponsiveCarousel = ({
  children,
  autoplay = true,
  autoplayDelay = 3000,
  pauseOnHover = true,
  infinite = true,
  showNavigation = true,
  showDots = true,
  className = '',
  spaceBetween = 20,
  centeredSlides = false,
  // Custom responsive breakpoints: Mobile(1), Tablet(2), Desktop(3)
  desktopSlides = 3,
  tabletSlides = 2,
  mobileSlides = 1,
  ...props
}) => {
  const slideCount = React.Children.count(children)
  
  // Disable loop if there are not enough slides for smooth looping
  // For loop to work properly, we need at least slidesPerView + 1 slides
  const shouldEnableLoop = infinite && slideCount > Math.max(desktopSlides, tabletSlides, mobileSlides)
  const swiperConfig = {
    modules: [Navigation, Pagination, A11y, Autoplay],
    spaceBetween,
    centeredSlides,
    loop: infinite,
    autoplay: autoplay
      ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: pauseOnHover,
        }
      : false,
    navigation: showNavigation
      ? {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }
      : false,
    pagination: showDots
      ? {
          clickable: true,
          dynamicBullets: true,
        }
      : false,
    // Enhanced touch behavior for smooth mobile experience
    touchRatio: 1.2,
    touchAngle: 30,
    grabCursor: true,
    resistanceRatio: 0.7,
    threshold: 5,
    // Speed and easing for smooth transitions
    speed: 400,
    // Enhanced mobile scrolling
    freeMode: false,
    // Responsive breakpoints - Mobile First approach
    breakpoints: {
      // Mobile: 320px to 767px (1 card with snap-to-center)
      320: {
        slidesPerView: mobileSlides,
        spaceBetween: spaceBetween * 0.5,
        centeredSlides: true,
        // Enhanced mobile-specific settings
        touchRatio: 1.5,
        resistanceRatio: 0.5,
        speed: 300,
        roundLengths: true,
      },
      // Tablet: 768px to 1023px (2 cards)
      768: {
        slidesPerView: tabletSlides,
        spaceBetween: spaceBetween * 0.75,
        centeredSlides: false,
        touchRatio: 1.2,
        speed: 350,
      },
      // Desktop: 1024px and up (3 cards)
      1024: {
        slidesPerView: desktopSlides,
        spaceBetween: spaceBetween,
        centeredSlides: false,
        touchRatio: 1,
        speed: 400,
      },
    },
    // Enhanced callback functions for better mobile UX
    on: {
      init: function (swiper) {
        // Add custom classes for styling
        swiper.el.classList.add('responsive-carousel')
        
        // Enable snap-to-center on mobile
        if (window.innerWidth < 768) {
          swiper.params.centeredSlides = true
          swiper.update()
        }
      },
      breakpoint: function (swiper) {
        // Handle breakpoint changes dynamically
        if (window.innerWidth < 768) {
          // Mobile: enable snap-to-center behavior
          swiper.params.centeredSlides = true
          swiper.params.touchRatio = 1.5
          swiper.params.speed = 300
        } else {
          swiper.params.centeredSlides = false
          swiper.params.touchRatio = 1.2
          swiper.params.speed = 400
        }
        swiper.update()
      },
      slideChange: function (swiper) {
        // Add smooth transition classes
        const slides = swiper.slides
        slides.forEach((slide, index) => {
          slide.classList.remove('slide-center', 'slide-left', 'slide-right')
          
          if (index === swiper.activeIndex) {
            slide.classList.add('slide-center')
          } else if (index < swiper.activeIndex) {
            slide.classList.add('slide-left')
          } else {
            slide.classList.add('slide-right')
          }
        })
      },
      touchStart: function (swiper) {
        // Enhanced touch feedback
        swiper.el.style.cursor = 'grabbing'
      },
      touchEnd: function (swiper) {
        swiper.el.style.cursor = 'grab'
      },
    },
    ...props,
  }

  return (
    <div className={`responsive-carousel-container ${className}`}>
      <Swiper {...swiperConfig}>
        {React.Children.map(children, (child, index) => (
          <SwiperSlide key={index} className="swiper-slide-custom">
            {child}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom styled navigation buttons */}
      {showNavigation && (
        <>
          <div className="swiper-button-prev carousel-nav-btn carousel-nav-prev">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </div>
          <div className="swiper-button-next carousel-nav-btn carousel-nav-next">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </>
      )}
    </div>
  )
}

export default ResponsiveCarousel